import z from 'zod';

import { createElement } from '@@plate/serialization/creators/createElement';
import { getElementTypes } from '@@plate/utils/getElementTypes';

import { ParagraphCodec, UnityParagraph } from './Paragraph';
import { PlateTextElements } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES } from '../constants';

export const UnityQuote = z.object({
    type: z.literal(ELEMENT_TYPES.QUOTE),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    quote: UnityParagraph.nullable(),
    caption: UnityParagraph.nullable(),
    version: Version,
});
export type UnityQuote = z.infer<typeof UnityQuote>;

export const PlateQuoteText = z.object({
    type: z.literal(ELEMENT_TYPES.QUOTE_TEXT),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});
export type QuoteTextElement = z.infer<typeof PlateQuoteText>;

export const QUOTE_TEXT_ELEMENT_TYPES = getElementTypes([PlateQuoteText]);

export const PlateQuoteCaption = z.object({
    type: z.literal(ELEMENT_TYPES.QUOTE_CAPTION),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});
export type QuoteCaptionElement = z.infer<typeof PlateQuoteCaption>;

export const QUOTE_CAPTION_ELEMENT_TYPES = getElementTypes([PlateQuoteCaption]);

export const PlateQuote = z.object({
    type: z.literal(ELEMENT_TYPES.QUOTE),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.tuple([PlateQuoteText, PlateQuoteCaption]),
});
export type QuoteElement = z.infer<typeof PlateQuote>;

export const QuoteCodec = z.codec(UnityQuote, PlateQuote, {
    decode: (value) => {
        const quoteText: QuoteTextElement = value.quote
            ? {
                  ...ParagraphCodec.decode(value.quote),
                  type: ELEMENT_TYPES.QUOTE_TEXT,
              }
            : createElement(ELEMENT_TYPES.QUOTE_TEXT, {
                  children: [{ text: '' }],
              });
        const quoteCaption: QuoteCaptionElement = value.caption
            ? {
                  ...ParagraphCodec.decode(value.caption),
                  type: ELEMENT_TYPES.QUOTE_CAPTION,
              }
            : createElement(ELEMENT_TYPES.QUOTE_CAPTION, {
                  children: [{ text: '' }],
              });
        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
            children: [quoteText, quoteCaption] satisfies [QuoteTextElement, QuoteCaptionElement],
        };
    },
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        quote: {
            ...ParagraphCodec.encode({
                ...value.children[0],
                type: ELEMENT_TYPES.PARAGRAPH,
            }),
        },
        caption: {
            ...ParagraphCodec.encode({
                ...value.children[1],
                type: ELEMENT_TYPES.PARAGRAPH,
            }),
        },
        ...value.data,
    }),
});
